import { ref } from 'vue'
import { defineStore } from 'pinia'
import { userApi } from '../api/user.api'
import { useQueryStore } from '@/modules/services/query/stores/query.store'
import { ActivationErrorCode, RegisterErrorCode, UserErrorCode } from '../types/user.enums'
import type {
  UserNotification,
  RegisterUser,
  User,
  UserProfile,
  UserSetting,
  ActivationUser,
} from '../models/user.model'
import type { HttpResponse } from '@/modules/services/http'
import type { Client } from '@/modules/clients/models/client.models'
import type { ProfileRequest } from '../api/user.requests'

export const useUserStore = defineStore('user', () => {
  const queryStore = useQueryStore()

  const user = ref<User>()
  const notifications = ref<UserNotification[]>([])

  const getActiveUserClient = (data: User) => {
    const accountKey = queryStore.getAccountKey()
    const account = data.clients.find((client) => client.clientKey.toLowerCase() === accountKey)

    return account ? account : data.activeClient
  }

  const isCurrentUserReady = async (): Promise<boolean> => {
    const response = await userApi.getCurrentUser()

    if (response.error || !response.data) return false

    user.value = response.data
    user.value.activeClient = getActiveUserClient(response.data)

    return true
  }

  const getUserProfile = async (): Promise<HttpResponse<UserProfile | null, UserErrorCode>> =>
    await userApi.getUserProfile()

  const getUserSettings = async (
    clientKey: string,
    userId?: number,
  ): Promise<HttpResponse<UserSetting, UserErrorCode>> =>
    await userApi.getUserSettings(clientKey, userId)

  const setActiveClient = (client: Client): void => {
    if (!user.value) return

    user.value.activeClient = client
  }

  const getUserActivation = async (
    userId?: number,
  ): Promise<HttpResponse<ActivationUser | null, UserErrorCode>> =>
    await userApi.getUserActivation(userId)

  const setActiveClientByKey = (clientKey: string): void => {
    if (!user.value) return
    if (user.value.activeClient?.clientKey === clientKey) return

    const client = user.value.clients.find(
      (client) => client.clientKey.toLowerCase() === clientKey.toLowerCase(),
    )

    if (client) {
      user.value.activeClient = client
    }
  }

  const activateCurrentUser = async (
    code: string,
  ): Promise<HttpResponse<null, ActivationErrorCode>> => {
    return await userApi.activateCurrentUser(code.toLowerCase())
  }

  const registerCurrentUser = async (
    request: RegisterUser,
  ): Promise<HttpResponse<null, RegisterErrorCode>> => {
    return await userApi.registerCurrentUser(request)
  }

  const updateCurrentUserProfile = async (request: ProfileRequest): Promise<boolean> => {
    const response = await userApi.updateCurrentUserProfile(request)

    if (response.error || !user.value) return false

    user.value.firstName = request.firstName
    user.value.lastName = request.lastName
    user.value.email = request.email
    user.value.phone = request.phone

    return true
  }

  const updateUserSettings = async (
    clientKey: string,
    request: UserSetting,
    userId?: number,
  ): Promise<HttpResponse<null, UserErrorCode>> =>
    await userApi.updateUserSettings(clientKey, request, userId)

  const notifyUser = (notification: UserNotification): void => {
    notifications.value.pop()
    notifications.value.push(notification)
  }

  return {
    user,
    notifications,
    isCurrentUserReady,
    getUserProfile,
    getUserActivation,
    getUserSettings,
    setActiveClient,
    setActiveClientByKey,
    activateCurrentUser,
    registerCurrentUser,
    updateCurrentUserProfile,
    updateUserSettings,
    notifyUser,
  }
})
